import { createSlice } from "@reduxjs/toolkit";
import {
  createMultiplesActividadesAplicacionPlantelThunk,
  getActividadAplicacionPlantelByIdThunk,
  getActividadesAplicacionPlantelThunk,
  toggleActividadAplicacionPlantelActiveThunk,
  updateActividadAplicacionPlantelThunk,
} from "./ActividadesAplicacionPlantel.thunks";

const initialState = {
  list: [],
  loading: false,
  error: null,
  page: 1,
  pageSize: 10,
  count: 0,
  next: null,
  previous: null,
  maxPageReached: 1,
  search: "",

  selectedActividad: null,
  selectedActividadLoading: false,
  selectedActividadError: null,
  viewModalOpen: false,

  formModalOpen: false,
  formMode: "create",
  formLoading: false,
  formError: null,

  confirmModalOpen: false,
  toggleActividadId: null,
  toggling: false,

  talentoFilter: "",
};

const actividadesAplicacionPlantelSlice = createSlice({
  name: "actividadesAplicacionPlantel",
  initialState,
  reducers: {
    clearActividadesAplicacionPlantelError: (state) => {
      state.error = null;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setSearch: (state, action) => {
      state.search = action.payload;
      state.page = 1;
    },
    resetActividadesAplicacionPlantel: () => initialState,
    openViewModal: (state) => {
      state.viewModalOpen = true;
    },
    closeViewModal: (state) => {
      state.viewModalOpen = false;
      state.selectedActividad = null;
    },
    openFormModal: (state, action) => {
      state.formModalOpen = true;
      state.formMode = action.payload?.mode || "create";
      if (action.payload?.actividad) {
        state.selectedActividad = action.payload.actividad;
      }
      state.formError = null;
    },
    closeFormModal: (state) => {
      state.formModalOpen = false;
      state.selectedActividad = null;
      state.formError = null;
    },
    openConfirmToggle: (state, action) => {
      state.confirmModalOpen = true;
      state.toggleActividadId = action.payload.id;
    },
    closeConfirmToggle: (state) => {
      state.confirmModalOpen = false;
      state.toggleActividadId = null;
    },
    setTalentoFilter: (state, action) => {
      state.talentoFilter = action.payload;
      state.page = 1;
    },
    clearFilters: (state) => {
      state.search = "";
      state.talentoFilter = "";
      state.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET ALL
      .addCase(getActividadesAplicacionPlantelThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getActividadesAplicacionPlantelThunk.fulfilled,
        (state, action) => {
          const { results, count, next, previous } = action.payload;
          state.loading = false;
          state.list = results;
          state.count = count;
          state.next = next;
          state.previous = previous;
          state.maxPageReached = Math.max(state.maxPageReached, state.page);
        },
      )
      .addCase(
        getActividadesAplicacionPlantelThunk.rejected,
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
        },
      )

      // GET BY ID
      .addCase(getActividadAplicacionPlantelByIdThunk.pending, (state) => {
        state.selectedActividadLoading = true;
        state.selectedActividadError = null;
      })
      .addCase(
        getActividadAplicacionPlantelByIdThunk.fulfilled,
        (state, action) => {
          state.selectedActividadLoading = false;
          state.selectedActividad = action.payload;
        },
      )
      .addCase(
        getActividadAplicacionPlantelByIdThunk.rejected,
        (state, action) => {
          state.selectedActividadLoading = false;
          state.selectedActividadError = action.payload;
        },
      )

      // CREATE MULTIPLES
      .addCase(
        createMultiplesActividadesAplicacionPlantelThunk.pending,
        (state) => {
          state.formLoading = true;
          state.formError = null;
        },
      )
      .addCase(
        createMultiplesActividadesAplicacionPlantelThunk.fulfilled,
        (state) => {
          state.formLoading = false;
          state.formModalOpen = false;
        },
      )
      .addCase(
        createMultiplesActividadesAplicacionPlantelThunk.rejected,
        (state, action) => {
          state.formLoading = false;
          state.formError = action.payload;
        },
      )

      // UPDATE
      .addCase(updateActividadAplicacionPlantelThunk.pending, (state) => {
        state.formLoading = true;
        state.formError = null;
      })
      .addCase(
        updateActividadAplicacionPlantelThunk.fulfilled,
        (state, action) => {
          state.formLoading = false;
          const index = state.list.findIndex((a) => a.id === action.payload.id);
          if (index !== -1) state.list[index] = action.payload;
          state.formModalOpen = false;
        },
      )
      .addCase(
        updateActividadAplicacionPlantelThunk.rejected,
        (state, action) => {
          state.formLoading = false;
          state.formError = action.payload;
        },
      )

      // TOGGLE ACTIVE
      .addCase(toggleActividadAplicacionPlantelActiveThunk.pending, (state) => {
        state.toggling = true;
        // Optimistic update
        const index = state.list.findIndex(
          (a) => a.id === state.toggleActividadId,
        );
        if (index !== -1) {
          state.list[index] = {
            ...state.list[index],
            is_active: !state.list[index].is_active,
          };
        }
      })
      .addCase(
        toggleActividadAplicacionPlantelActiveThunk.fulfilled,
        (state, action) => {
          state.toggling = false;
          state.confirmModalOpen = false;
          const index = state.list.findIndex((a) => a.id === action.payload.id);
          if (index !== -1) state.list[index] = action.payload;
        },
      )
      .addCase(
        toggleActividadAplicacionPlantelActiveThunk.rejected,
        (state, action) => {
          state.toggling = false;
          state.error = action.payload;
          // Revertir optimistic update
          const index = state.list.findIndex(
            (a) => a.id === state.toggleActividadId,
          );
          if (index !== -1) {
            state.list[index] = {
              ...state.list[index],
              is_active: !state.list[index].is_active,
            };
          }
        },
      );
  },
});

export const {
  clearActividadesAplicacionPlantelError,
  setPage,
  setSearch,
  resetActividadesAplicacionPlantel,
  openViewModal,
  closeViewModal,
  openFormModal,
  closeFormModal,
  openConfirmToggle,
  closeConfirmToggle,
  setTalentoFilter,
  clearFilters,
} = actividadesAplicacionPlantelSlice.actions;

export default actividadesAplicacionPlantelSlice.reducer;
